import { computed, Injectable, signal } from '@angular/core';
import { associateWith, QueueSet } from '@ps-gui/shared-utils';

const debugPrintIds = (message: string, ids: Iterable<string>) =>
  console.debug(
    message,
    [...ids].map((id) => id.substring(0, 3)),
  );

const sliceFromPivotOutwards = <T>(
  strings: T[],
  pivotIndex: number,
  fromInclusive: number,
  toExclusive: number,
) => {
  const halfSize = Math.max(pivotIndex - fromInclusive, toExclusive - pivotIndex);

  const result = [];
  result.push(strings[pivotIndex]);
  for (let i = 1; i < halfSize; i++) {
    if (pivotIndex - i >= fromInclusive) result.push(strings[pivotIndex - i]);
    if (pivotIndex + i < toExclusive) result.push(strings[pivotIndex + i]);
  }
  return result;
};

/**
 * Utility to decide which images to load / unload given
 * which image is in "focus"
 */
@Injectable({
  providedIn: 'root',
})
export class PageImageLoadingPrioritizer {
  private maxStoredImages = 100;
  private toleratedWindowHalfSize = 10;
  private loadWindowHalfSize = 20;

  private readonly loadedPageIds = new QueueSet<string>();

  private readonly pageIds = signal<string[]>([]);
  private readonly pageIndexById = computed(() =>
    associateWith(this.pageIds(), (_, index) => index),
  );

  reconfigure(
    maxStoredImages: number,
    toleratedWindowHalfSize: number,
    loadWindowHalfSize: number,
  ) {
    this.maxStoredImages = maxStoredImages;
    this.toleratedWindowHalfSize = toleratedWindowHalfSize;
    this.loadWindowHalfSize = loadWindowHalfSize;
  }

  init(pageIds: string[]) {
    this.pageIds.set([...pageIds]);
    console.debug('>>>>>>>>>>>>>>>>>>>>>');
    console.debug('Initialized image loading prioritizer with page ids: ', pageIds);
    console.debug('>>>>>>>>>>>>>>>>>>>>>');
  }

  isLoaded(pageId: string) {
    return this.loadedPageIds.has(pageId);
  }

  requestLoadAroundPage(pageId: string): {
    pageIdsToLoad: Iterable<string>;
    pageIdsToUnload: string[];
  } {
    console.debug('--------------------');
    debugPrintIds('request load around: ', [pageId]);
    debugPrintIds('current loaded pages: ', [...this.loadedPageIds.values()]);

    const pivotIndex = this.pageIndexById()[pageId];

    if (!this.existsMissingPageInToleratedWindow(pivotIndex))
      return { pageIdsToLoad: [], pageIdsToUnload: [] };

    const missingPageIds = this.getMissingPageIdsInLoadWindow(pivotIndex);

    debugPrintIds('missing page ids: ', missingPageIds);

    missingPageIds.forEach((pageId) => this.loadedPageIds.enqueue(pageId));

    const overflow = this.loadedPageIds.length() - this.maxStoredImages;

    const idsBefore = [...this.loadedPageIds.values()];
    const pageIdsToUnload = [...this.loadedPageIds.dequeueMany(overflow)];

    if (pageIdsToUnload.length > 0) {
      debugPrintIds('pages before unload: ', idsBefore);
      debugPrintIds(`OVERFLOW of ${overflow}, unloaded: `, pageIdsToUnload);
      debugPrintIds('pages after unload: ', [...this.loadedPageIds.values()]);
    }

    return {
      pageIdsToLoad: missingPageIds,
      pageIdsToUnload: pageIdsToUnload,
    };
  }

  private existsMissingPageInToleratedWindow(pivotPageIndex: number): boolean {
    const toleratedLeftBound = Math.max(0, pivotPageIndex - this.toleratedWindowHalfSize);
    const toleratedRightBound = Math.min(
      this.pageIds().length,
      pivotPageIndex + this.toleratedWindowHalfSize + 1,
    );

    for (let i = toleratedLeftBound; i < toleratedRightBound; i++) {
      if (!this.loadedPageIds.has(this.pageIds()[i])) {
        debugPrintIds('missing page in tolerated window: ', [this.pageIds()[i]]);
        return true;
      }
    }

    return false;
  }

  private getMissingPageIdsInLoadWindow(pivotIndex: number) {
    const loadLeftBound = Math.max(0, pivotIndex - this.loadWindowHalfSize);
    const loadRightBound = Math.min(
      this.pageIds().length,
      pivotIndex + this.loadWindowHalfSize + 1,
    );

    return sliceFromPivotOutwards(this.pageIds(), pivotIndex, loadLeftBound, loadRightBound).filter(
      (pageId) => !this.loadedPageIds.has(pageId),
    );
  }
}
